import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
} from "@mui/material";
import {
  useAddSettingMutation,
  useLazyGetSettingsQuery,
} from "../../services/settings";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isNumber } from "../../utils/validations";
import { showError, showToast } from "../../helpers";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import Pagination from "../../components/Panigation";
import SwitchToggle from "../../components/SwitchToggle";
import { NOTIFICATION_TEMPLATE_TYPE } from "../../constants/enums";
import { useLazyGetEmailTemplateListQuery, useUpdateEmailTemplateMutation, useDeleteEmailTemplateMutation } from "../../services/emailTemplate";
import DeleteConfirmationModal from "../../components/DeleteModal";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const validationSchema = Yup.object({
  maxVouchers: Yup.number()
    .typeError("Maximum Vouchers must be a number")
    .required("Maximum Vouchers is required")
    .min(1, "Must be at least 1"),
  expirationPeriod: Yup.number()
    .typeError("Expiration Period must be a number")
    .required("Expiration Period is required")
    .min(1, "Must be at least 1 day"),
  referralEarningMethod: Yup.string().required(
    "Referral Earning Method is required"
  ),
});

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

const ManageSystemSettings = () => {
  const navigate = useNavigate();
  const [getSettingApi] = useLazyGetSettingsQuery();
  const [updateSetting] = useAddSettingMutation();

  const [valueTabs, setValueTabs] = React.useState(1);
  // const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
  //   setValueTabs(newValue);
  // };

  // Email Templates list states
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedId, setSelectedId] = useState<string>("");
  const [openDelete, setOpenDelete] = useState(false);

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  }

  const onPageChange = (newPage: number) => setPage(newPage);

  const [triggerEmailList, { data: emailListRes, isFetching: isEmailFetching }] =
    useLazyGetEmailTemplateListQuery();
  const [updateEmailTemplate, { isLoading: isUpdatingTemplate }] = useUpdateEmailTemplateMutation();
  const [deleteEmailTemplate, { isLoading: isDeletingTemplate }] = useDeleteEmailTemplateMutation();

  const formik = useFormik({
    initialValues: {
      maxVouchers: "",
      expirationPeriod: "",
      referralEarningMethod: "",
      id: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const body = {
        global: {
          maxVouchersPerUserPerReward: Number(values.maxVouchers),
          pointsExpirationPeriodDays: Number(values.expirationPeriod),
          referralEarningMethod: Number(values.referralEarningMethod),
        },
      };
      try {
        const response = await updateSetting({ body }).unwrap();
        if (response?.statusCode === 200) {
          showToast(response?.message || "System setting updated suceessfully");
        }
      } catch (error: any) {
        showError(
          error?.message ||
          "Something went wrong. System settings were not updated."
        );
      }
    },
  });

  const getSetting = async () => {
    try {
      const response = await getSettingApi({}).unwrap();
      if (response?.statusCode === 200) {
        formik.setValues({
          maxVouchers: response.data?.global?.maxVouchersPerUserPerReward,
          expirationPeriod: response.data?.global?.pointsExpirationPeriodDays,
          referralEarningMethod: response.data?.global?.referralEarningMethod,
          id: response.data?.id,
        });
      }
      console.log(response);
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    getSetting();
  }, []);

  useEffect(() => {
    // Trigger email templates list fetch
    triggerEmailList({ page, limit, search: debouncedSearchTerm });
  }, [triggerEmailList, page, limit, debouncedSearchTerm]);

  const emailTemplates = useMemo(() => {
    const items: any[] = Array.isArray((emailListRes as any)?.data?.emailtemplate)
      ? (emailListRes as any).data.emailtemplate
      : Array.isArray((emailListRes as any)?.data)
        ? ((emailListRes as any).data as any[])
        : [];
    return items.map((t: any) => ({
      ...t,
      id: t?._id || t?.id,
    }));
  }, [emailListRes]);

  const totalItems = (emailListRes as any)?.data?.count || emailTemplates.length || 0;
  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / (limit || 10)));

  const handleToggleTemplate = async (tpl: any, nextChecked: boolean) => {
    const id = tpl?._id || tpl?.id;
    if (!id) return;
    try {
      await updateEmailTemplate({ body: { isActive: nextChecked } as any, id }).unwrap();
      triggerEmailList({ page, limit, search: debouncedSearchTerm });
    } catch (e) {
      console.log(e);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!selectedId) return;
    try {
      await deleteEmailTemplate({ id: selectedId }).unwrap();
      showToast("Email template deleted successfully");
      toggleDeleteModal();
      triggerEmailList({ page, limit, search: debouncedSearchTerm });
    } catch (error: any) {
      showError(error?.data?.message || error?.message || "Failed to delete email template");
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Email Template</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Email Template
        </p>
      </div>
      <div className="table_header">
        {/* <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Global Controls" {...a11yProps(0)} />
              <Tab label="Email Template Editor" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div> */}
      </div>
      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" action="" onSubmit={formik.handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="setting_maxVouchers">
                  Maximum Vouchers per User per Reward
                </label>
                <TextField
                  id="setting_maxVouchers"
                  placeholder="Enter Maximum Vouchers"
                  type="text"
                  fullWidth
                  hiddenLabel
                  name="maxVouchers"
                  value={formik.values.maxVouchers}
                  onChange={(e) => {
                    if (/^0\d+/.test(e.target.value)) return;
                    if (isNumber(e.target.value)) {
                      formik.handleChange(e);
                    }
                  }}
                  error={
                    formik.touched.maxVouchers &&
                    Boolean(formik.errors.maxVouchers)
                  }
                  helperText={
                    formik.touched.maxVouchers && formik.errors.maxVouchers
                  }
                />
              </div>
              <div className="control_group w_50">
                <label htmlFor="setting_expiration">
                  Default Expiration Period for Points (days)
                </label>
                <TextField
                  id="setting_expiration"
                  placeholder="Enter Default Expiration Period for Points"
                  type="text"
                  name="expirationPeriod"
                  value={formik.values.expirationPeriod}
                  fullWidth
                  hiddenLabel
                  onChange={(e) => {
                    if (/^0\d+/.test(e.target.value)) return;
                    if (isNumber(e.target.value)) {
                      formik.handleChange(e);
                    }
                  }}
                  error={
                    formik.touched.expirationPeriod &&
                    Boolean(formik.errors.expirationPeriod)
                  }
                  helperText={
                    formik.touched.expirationPeriod &&
                    formik.errors.expirationPeriod
                  }
                />
              </div>
              <div className="control_group">
                <label htmlFor="setting_referral">
                  Referral Earning Method
                </label>
                <RadioGroup
                  className="flex"
                  aria-labelledby="demo-radio-Referral-group-label"
                  defaultValue="register"
                  // name="radio-Referral-group"
                  name="referralEarningMethod"
                  value={formik.values.referralEarningMethod}
                  onChange={formik.handleChange}
                >
                  <FormControlLabel
                    value="1"
                    control={<Radio />}
                    label="On Register"
                  />
                  <FormControlLabel
                    value="2"
                    control={<Radio />}
                    label="On First Purchase"
                  />
                </RadioGroup>
                {formik.touched.referralEarningMethod &&
                  formik.errors.referralEarningMethod && (
                    <Typography
                      className="error_text"
                      variant="caption"
                      color="red"
                    >
                      {formik.errors.referralEarningMethod}
                    </Typography>
                  )}
              </div>
            </div>
            <div className="form_btn">
              <Button type="submit" className="btn btn_primary">
                Submit
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  placeholder="Search..."
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                />
              </Box>
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => navigate("/manage-email-template/add-email-template")}
              >
                <AddIcon /> Add Email Template
              </Button>
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Template Type</TableCell>
                  <TableCell>Date Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emailTemplates.map((row: any) => (
                  <TableRow hover key={row.id}>
                    <TableCell>{row?.name || '-'}</TableCell>
                    <TableCell className="max_c2">{row?.title || '-'}</TableCell>
                    <TableCell>{
                      (() => {
                        const entries = Object.entries(NOTIFICATION_TEMPLATE_TYPE).filter(([k]) => isNaN(Number(k)));
                        const found = entries.find(([, v]) => Number(v) === Number(row?.templateType));
                        return found ? found[0] : row?.templateType || '-';
                      })()
                    }</TableCell>
                    <TableCell>{row?.createdAt ? new Date(row.createdAt).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={!!row?.isActive}
                        onChange={(checked) => {
                          if (isUpdatingTemplate) return;
                          handleToggleTemplate(row, checked)
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton onClick={() => navigate(`/manage-email-template/edit-email-template/${row?._id}`)}>
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>
                        <IconButton onClick={() => {
                          setSelectedId(row?._id);
                          toggleDeleteModal();
                        }}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

          </TableContainer>
          <Pagination
            module={emailTemplates || []}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        </CustomTabPanel>
      </div>
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDeleteTemplate}
        header="Are you sure you want to delete this email template?"
      />
    </>
  );
};

export default ManageSystemSettings;
